import QRCode from "qrcode";
import { useEffect, useState } from "react";
import type { LeaderboardEntry, Participant, RoomPublicState } from "../shared/types";

export function Brand({ compact = false, showSuffix = true }: { compact?: boolean; showSuffix?: boolean }) {
  return <a className={`brand ${compact ? "brand--compact" : ""}`} href="/" aria-label="PODIYOUM home">
    <span className="brand-logo-frame"><img src="/podiyoum-logo.png" alt="PODIYOUM" /></span>
    {!compact && showSuffix && <span className="brand-suffix">BOOTH</span>}
  </a>;
}

export function Loading() {
  return <main className="center-screen"><div className="spinner" /><p>Getting PODIYOUM ready…</p></main>;
}

export function ErrorBanner({ message, onClose }: { message?: string; onClose?: () => void }) {
  if (!message) return null;
  return <div className="error-banner" role="alert"><span>{message}</span>{onClose && <button onClick={onClose} aria-label="Dismiss">×</button>}</div>;
}

export function StatusPill({ connected }: { connected: boolean }) {
  return <span className={`status-pill ${connected ? "status-pill--online" : ""}`}><i />{connected ? "Live" : "Reconnecting"}</span>;
}

export function Participants({ participants, large = false }: { participants: Array<Pick<Participant, "id" | "nickname" | "icon">>; large?: boolean }) {
  return <div className={`participant-grid ${large ? "participant-grid--large" : ""}`}>
    {participants.map((participant) => <div className="participant-chip" key={participant.id}>
      <span>{participant.icon}</span><strong>{participant.nickname}</strong>
    </div>)}
  </div>;
}

export function QrCard({ joinUrl, joinCode, showDetails = true }: { joinUrl: string; joinCode: string; showDetails?: boolean }) {
  const [src, setSrc] = useState("");
  useEffect(() => { void QRCode.toDataURL(joinUrl, { width: 512, margin: 1, errorCorrectionLevel: "M", color: { dark: "#101010", light: "#ffffff" } }).then(setSrc); }, [joinUrl]);
  return <div className="qr-card">
    {src ? <img src={src} alt={`QR code to join ${joinCode}`} /> : <div className="qr-placeholder" />}
    {showDetails && <div className="qr-card__details">
      <small>JOIN AT</small>
      <strong className="join-domain">join.podiyoum.com</strong>
      <small>JOIN CODE</small>
      <strong className="join-code">{joinCode}</strong>
    </div>}
  </div>;
}

export function Countdown({ endsAt }: { endsAt?: number }) {
  const [now, setNow] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(timer);
  }, []);
  const remaining = Math.max(0, Math.ceil(((endsAt ?? now) - now) / 1000));
  return <div className="countdown" aria-live="assertive">{remaining || "GO!"}</div>;
}

function formatTime(ms?: number) {
  if (ms === undefined) return "—";
  return `${(ms / 1000).toFixed(1)}s`;
}

export function Leaderboard({ entries, showAll = false }: { entries: LeaderboardEntry[]; showAll?: boolean }) {
  const shown = showAll ? entries : entries.slice(0, 3);
  const medals = ["🥇", "🥈", "🥉"];
  return <div className={`leaderboard ${showAll ? "leaderboard--all" : "leaderboard--podium"}`}>
    {shown.length === 0 && <p className="muted">No eligible players in this run.</p>}
    {shown.map((entry) => <div className="leader-row" key={entry.participantId}>
      <span className="leader-rank">{medals[entry.rank - 1] ?? entry.rank}</span>
      <span className="leader-icon">{entry.icon}</span>
      <strong>{entry.nickname}</strong>
      <span className="leader-detail">{entry.completed ? formatTime(entry.elapsedMs) : `${entry.progress} progress`}</span>
    </div>)}
  </div>;
}

export function JoinedIdentity({ state }: { state: RoomPublicState }) {
  if (!state.player) return null;
  return <div className="identity-card">
    <span className="identity-icon">{state.player.participant.icon}</span>
    <div><small>YOU’RE IN</small><strong>{state.player.participant.nickname}</strong></div>
    <span className="joined-check">✓</span>
  </div>;
}
